import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { format, differenceInDays, startOfMonth, endOfMonth, eachDayOfInterval, addDays, startOfQuarter, endOfQuarter, addQuarters, eachWeekOfInterval, eachMonthOfInterval } from 'date-fns';
import Icon from '../../../components/AppIcon';
import { parseLocalDate } from '../../../utils/dateUtils';

const STATUS_CONFIG = {
  not_started: { label: 'Not Started', cls: 'bg-gray-100 text-gray-600' },
  in_progress: { label: 'In Progress', cls: 'bg-blue-100 text-blue-700' },
  completed: { label: 'Completed', cls: 'bg-green-100 text-green-700' },
  blocked: { label: 'Blocked', cls: 'bg-red-100 text-red-700' },
  on_hold: { label: 'On Hold', cls: 'bg-yellow-100 text-yellow-700' },
};

const ROW_TYPE_COLORS = {
  deliverable: { bar: 'bg-purple-500', bg: 'bg-purple-50/40', dot: 'bg-purple-500', text: 'text-purple-700', badge: 'bg-purple-100 text-purple-700' },
  task: { bar: 'bg-blue-500', bg: 'bg-blue-50/30', dot: 'bg-blue-500', text: 'text-blue-700', badge: 'bg-blue-100 text-blue-700' },
  subtask: { bar: 'bg-emerald-500', bg: 'bg-emerald-50/20', dot: 'bg-emerald-500', text: 'text-emerald-700', badge: 'bg-emerald-100 text-emerald-700' },
  resource: { bar: 'bg-indigo-400', bg: 'bg-indigo-50/20', dot: 'bg-indigo-400', text: 'text-indigo-700', badge: 'bg-indigo-100 text-indigo-700' },
};

const SORT_OPTIONS = [
  { value: 'name_asc', label: 'Name A→Z' },
  { value: 'name_desc', label: 'Name Z→A' },
  { value: 'start_asc', label: 'Start Date ↑' },
  { value: 'start_desc', label: 'Start Date ↓' },
  { value: 'status', label: 'Status' },
];

const ALL_COLUMNS = [
  { id: 'resource', label: 'Resource', minWidth: 100, defaultWidth: 140 },
  { id: 'startDate', label: 'Start', minWidth: 55, defaultWidth: 76 },
  { id: 'endDate', label: 'End', minWidth: 55, defaultWidth: 76 },
  { id: 'status', label: 'Status', minWidth: 70, defaultWidth: 90 },
  { id: 'hours', label: 'Hours', minWidth: 45, defaultWidth: 60 },
];

const DEFAULT_NAME_WIDTH = 220;

const TIME_SCALE_OPTIONS = [
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
];

const TIME_SCALE_PIXELS = {
  daily: 24,
  weekly: 20,
  monthly: 16,
  quarterly: 12,
};

const getStatusBadge = (status) => STATUS_CONFIG?.[status] || { label: status || '—', cls: 'bg-gray-100 text-gray-500' };

// ─── Column resize handle ───────────────────────────────────────────────────
const ResizeHandle = ({ onResize }) => {
  const dragging = useRef(false);
  const startX = useRef(0);

  const onMouseDown = (e) => {
    e?.preventDefault();
    dragging.current = true;
    startX.current = e?.clientX;
    const onMouseMove = (ev) => {
      if (!dragging?.current) return;
      const delta = ev?.clientX - startX?.current;
      startX.current = ev?.clientX;
      onResize(delta);
    };
    const onMouseUp = () => {
      dragging.current = false;
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
  };

  return (
    <div
      onMouseDown={onMouseDown}
      className="absolute right-0 top-0 bottom-0 w-2 cursor-col-resize flex items-center justify-center hover:bg-primary/20 group z-10"
      style={{ userSelect: 'none' }}
    >
      <div className="w-0.5 h-4 bg-border group-hover:bg-primary/60 rounded" />
    </div>
  );
};

const ColumnPicker = ({ visibleColumns, onChange, onClose }) => (
  <div className="absolute right-0 top-full mt-1 bg-card border border-border rounded-lg shadow-lg z-50 p-3 w-44">
    <p className="text-xs font-semibold text-foreground mb-2">Show Columns</p>
    {ALL_COLUMNS?.map(col => (
      <label key={col?.id} className="flex items-center gap-2 py-1 cursor-pointer hover:bg-muted/50 rounded px-1">
        <input
          type="checkbox"
          checked={visibleColumns?.includes(col?.id)}
          onChange={() => onChange(col?.id)}
          className="w-3 h-3 rounded"
        />
        <span className="text-xs text-foreground">{col?.label}</span>
      </label>
    ))}
    <button onClick={onClose} className="mt-2 w-full text-xs text-center text-muted-foreground hover:text-foreground py-1">Close</button>
  </div>
);

// ─── Tooltip ────────────────────────────────────────────────────────────────
const GanttTooltip = ({ row, visible, x, y }) => {
  if (!visible || !row) return null;
  const isResource = row?._kind === 'resource';
  const isPhase = row?._kind === 'phase';
  const statusBadge = getStatusBadge(row?.status);
  const rowType = row?.rowType || 'deliverable';
  const colors = ROW_TYPE_COLORS?.[rowType] || ROW_TYPE_COLORS?.deliverable;
  const duration = row?.startDate && row?.endDate
    ? differenceInDays(parseLocalDate(row?.endDate), parseLocalDate(row?.startDate)) + 1
    : 0;

  const typeLabel = isResource ? 'Resource Allocation' : isPhase ? 'Phase' :
    rowType === 'deliverable' ? 'Deliverable' : rowType === 'task' ? 'Task' : 'Sub-task';

  return (
    <div
      className="fixed z-[9999] bg-popover border border-border rounded-lg shadow-xl p-3 pointer-events-none"
      style={{ left: x + 12, top: y - 10, minWidth: '220px', maxWidth: '280px' }}
    >
      <div className="flex items-start gap-2 mb-2">
        <span className={`mt-0.5 w-2 h-2 rounded-full flex-shrink-0 ${
          isPhase ? 'bg-amber-500' : isResource ? 'bg-indigo-400' : colors?.dot
        }`} />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-foreground leading-tight break-words">{row?.name}</p>
          <span className={`inline-block text-xs px-1.5 py-0.5 rounded mt-1 ${
            isPhase ? 'bg-amber-100 text-amber-700' : isResource ? 'bg-indigo-100 text-indigo-700' : colors?.badge
          }`}>{typeLabel}</span>
        </div>
      </div>
      <div className="space-y-1 text-xs">
        {!isPhase && (
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">Status</span>
            <span className={`px-1.5 py-0.5 rounded ${statusBadge?.cls}`}>{statusBadge?.label}</span>
          </div>
        )}
        <div className="flex items-center justify-between">
          <span className="text-muted-foreground">Start</span>
          <span className="text-foreground font-medium">{row?.startDate ? format(parseLocalDate(row?.startDate), 'MMM d, yyyy') : '—'}</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-muted-foreground">End</span>
          <span className="text-foreground font-medium">{row?.endDate ? format(parseLocalDate(row?.endDate), 'MMM d, yyyy') : '—'}</span>
        </div>
        {duration > 0 && (
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">Duration</span>
            <span className="text-foreground font-medium">{duration} day{duration !== 1 ? 's' : ''}</span>
          </div>
        )}
        {isResource && row?._allocationHours && (
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">Allocated Hours</span>
            <span className="text-foreground font-medium">{row?._allocationHours}h</span>
          </div>
        )}
        {isResource && row?.category && (
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">Category</span>
            <span className="text-foreground font-medium">{row?.category}</span>
          </div>
        )}
        {!isResource && !isPhase && row?._resourceCount > 0 && (
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">Resources</span>
            <span className="text-foreground font-medium">{row?._resourceCount} assigned</span>
          </div>
        )}
      </div>
      {!isPhase && (
        <p className="text-xs text-muted-foreground mt-2 pt-1 border-t border-border">Click to edit dates • Drag to reschedule</p>
      )}
    </div>
  );
};

// ─── Inline date editor popover ──────────────────────────────────────────────
const DateEditPopover = ({ row, x, y, onSave, onClose, saving }) => {
  const [startDate, setStartDate] = useState(row?.startDate ? row?.startDate?.slice(0, 10) : '');
  const [endDate, setEndDate] = useState(row?.endDate ? row?.endDate?.slice(0, 10) : '');
  const ref = useRef(null);

  useEffect(() => {
    const handleClick = (e) => {
      if (ref?.current && !ref?.current?.contains(e?.target)) onClose();
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [onClose]);

  const handleSave = () => {
    if (startDate && endDate) onSave(startDate, endDate);
  };

  const style = { position: 'fixed', zIndex: 9999, left: Math.min(x, window.innerWidth - 260), top: Math.min(y, window.innerHeight - 200) };

  return (
    <div ref={ref} className="bg-popover border border-border rounded-lg shadow-xl p-3 w-56" style={style}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-semibold text-foreground">Edit Date Range</span>
        <button onClick={onClose} className="text-muted-foreground hover:text-foreground p-0.5 rounded">
          <Icon name="X" size={12} />
        </button>
      </div>
      <p className="text-xs text-muted-foreground mb-2 truncate font-medium">{row?.name}</p>
      <div className="space-y-2">
        <div>
          <label className="text-xs text-muted-foreground block mb-0.5">Start Date</label>
          <input
            type="date"
            value={startDate}
            onChange={e => setStartDate(e?.target?.value)}
            className="w-full text-xs border border-border rounded px-2 py-1 bg-background text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
          />
        </div>
        <div>
          <label className="text-xs text-muted-foreground block mb-0.5">End Date</label>
          <input
            type="date"
            value={endDate}
            min={startDate}
            onChange={e => setEndDate(e?.target?.value)}
            className="w-full text-xs border border-border rounded px-2 py-1 bg-background text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
          />
        </div>
      </div>
      <div className="flex gap-2 mt-3">
        <button
          onClick={handleSave}
          disabled={saving || !startDate || !endDate}
          className="flex-1 text-xs bg-primary text-primary-foreground rounded px-2 py-1 hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-1"
        >
          {saving ? <Icon name="Loader2" size={11} className="animate-spin" /> : <Icon name="Check" size={11} />}
          {saving ? 'Saving…' : 'Save'}
        </button>
        <button onClick={onClose} className="flex-1 text-xs border border-border rounded px-2 py-1 text-muted-foreground hover:text-foreground hover:bg-muted">
          Cancel
        </button>
      </div>
    </div>
  );
};

// ─── Inline name editor ──────────────────────────────────────────────────────
const InlineNameEditor = ({ value, onSave, onCancel }) => {
  const [text, setText] = useState(value || '');
  const inputRef = useRef(null);

  useEffect(() => {
    inputRef?.current?.focus();
    inputRef?.current?.select();
  }, []);

  const commit = () => {
    const trimmed = text?.trim();
    if (trimmed && trimmed !== value) onSave(trimmed);
    else onCancel();
  };

  const handleKeyDown = (e) => {
    if (e?.key === 'Enter') { e?.preventDefault(); commit(); }
    if (e?.key === 'Escape') onCancel();
  };

  return (
    <input
      ref={inputRef}
      value={text}
      onChange={e => setText(e?.target?.value)}
      onBlur={commit}
      onKeyDown={handleKeyDown}
      className="flex-1 min-w-0 text-xs border border-primary rounded px-1 py-0 bg-background text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
      style={{ height: '18px' }}
      onClick={e => e?.stopPropagation()}
    />
  );
};

// ─── Inline status dropdown ──────────────────────────────────────────────────
const InlineStatusDropdown = ({ currentStatus, onSave, onClose }) => {
  const ref = useRef(null);

  useEffect(() => {
    const handleClick = (e) => {
      if (ref?.current && !ref?.current?.contains(e?.target)) onClose();
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [onClose]);

  return (
    <div
      ref={ref}
      className="absolute top-full left-0 mt-0.5 bg-popover border border-border rounded-lg shadow-xl z-[9999] py-1 w-36"
    >
      {Object.entries(STATUS_CONFIG)?.map(([key, cfg]) => (
        <button
          key={key}
          onClick={() => { onSave(key); onClose(); }}
          className={`w-full text-left px-2 py-1 text-xs hover:bg-muted flex items-center gap-2 ${currentStatus === key ? 'font-semibold' : ''}`}
        >
          <span className={`w-2 h-2 rounded-full flex-shrink-0 ${cfg?.cls?.split(' ')?.[0]}`} />
          {cfg?.label}
          {currentStatus === key && <Icon name="Check" size={10} className="ml-auto text-primary" />}
        </button>
      ))}
    </div>
  );
};

// ─── Inline date cell editor ─────────────────────────────────────────────────
const InlineDateCell = ({ value, onChange, minDate: minVal = undefined, label }) => {
  const [editing, setEditing] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (editing) inputRef?.current?.focus();
  }, [editing]);

  if (editing) {
    return (
      <input
        ref={inputRef}
        type="date"
        defaultValue={value ? value?.slice(0, 10) : ''}
        min={minVal}
        onBlur={(e) => {
          const v = e?.target?.value;
          if (v && v !== value?.slice(0, 10)) onChange(v);
          setEditing(false);
        }}
        onKeyDown={(e) => {
          if (e?.key === 'Escape') setEditing(false);
          if (e?.key === 'Enter') e?.target?.blur();
        }}
        className="w-full text-xs border border-primary rounded px-1 py-0 bg-background text-foreground focus:outline-none"
        style={{ height: '18px' }}
        onClick={e => e?.stopPropagation()}
      />
    );
  }

  return (
    <button
      onClick={() => setEditing(true)}
      className="text-xs text-muted-foreground hover:text-foreground hover:underline cursor-pointer w-full text-left"
      title={`Click to edit ${label}`}
    >
      {value ? format(parseLocalDate(value), 'MMM d') : <span className="text-muted-foreground/50">—</span>}
    </button>
  );
};

// ─── Draggable + Resizable Gantt bar ─────────────────────────────────────────
const DraggableBar = ({ row, barStyle, isPhase, isResource, colors, pixelsPerDay, minDate, onDateChange, onBarMouseEnter, onBarMouseMove, onBarMouseLeave, onBarClick, savingDateId }) => {
  const isDragging = useRef(false);
  const isResizingLeft = useRef(false);
  const isResizingRight = useRef(false);
  const dragStartX = useRef(0);
  const originalStartDate = useRef(null);
  const originalEndDate = useRef(null);
  const [dragOffset, setDragOffset] = useState(0);
  const [resizeLeftOffset, setResizeLeftOffset] = useState(0);
  const [resizeRightOffset, setResizeRightOffset] = useState(0);
  const [interacting, setInteracting] = useState(false);
  const isSaving = savingDateId === row?.id;

  // ── Drag (move) ──
  const handleMouseDown = (e) => {
    if (isPhase || !onDateChange) return;
    // Don't start drag if clicking resize handles
    if (e?.target?.dataset?.resize) return;
    e?.preventDefault();
    e?.stopPropagation();
    isDragging.current = true;
    dragStartX.current = e?.clientX;
    originalStartDate.current = row?.startDate;
    originalEndDate.current = row?.endDate;
    setInteracting(true);

    const onMouseMove = (ev) => {
      if (!isDragging?.current) return;
      const delta = ev?.clientX - dragStartX?.current;
      setDragOffset(delta);
    };

    const onMouseUp = (ev) => {
      if (!isDragging?.current) return;
      isDragging.current = false;
      setInteracting(false);
      const delta = ev?.clientX - dragStartX?.current;
      const daysDelta = Math.round(delta / pixelsPerDay);
      if (daysDelta !== 0 && originalStartDate?.current && originalEndDate?.current) {
        const newStart = addDays(parseLocalDate(originalStartDate?.current), daysDelta);
        const newEnd = addDays(parseLocalDate(originalEndDate?.current), daysDelta);
        onDateChange(row?.id, format(newStart, 'yyyy-MM-dd'), format(newEnd, 'yyyy-MM-dd'));
      }
      setDragOffset(0);
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };

    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
  };

  // ── Resize left edge (change start date) ──
  const handleResizeLeftMouseDown = (e) => {
    if (isPhase || !onDateChange) return;
    e?.preventDefault();
    e?.stopPropagation();
    isResizingLeft.current = true;
    dragStartX.current = e?.clientX;
    originalStartDate.current = row?.startDate;
    originalEndDate.current = row?.endDate;
    setInteracting(true);

    const onMouseMove = (ev) => {
      if (!isResizingLeft?.current) return;
      const delta = ev?.clientX - dragStartX?.current;
      setResizeLeftOffset(delta);
    };

    const onMouseUp = (ev) => {
      if (!isResizingLeft?.current) return;
      isResizingLeft.current = false;
      setInteracting(false);
      const delta = ev?.clientX - dragStartX?.current;
      const daysDelta = Math.round(delta / pixelsPerDay);
      if (daysDelta !== 0 && originalStartDate?.current && originalEndDate?.current) {
        const newStart = addDays(parseLocalDate(originalStartDate?.current), daysDelta);
        const endD = parseLocalDate(originalEndDate?.current);
        // Don't allow start to go past end
        if (newStart < endD) {
          onDateChange(row?.id, format(newStart, 'yyyy-MM-dd'), format(endD, 'yyyy-MM-dd'));
        }
      }
      setResizeLeftOffset(0);
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };

    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
  };

  // ── Resize right edge (change end date) ──
  const handleResizeRightMouseDown = (e) => {
    if (isPhase || !onDateChange) return;
    e?.preventDefault();
    e?.stopPropagation();
    isResizingRight.current = true;
    dragStartX.current = e?.clientX;
    originalStartDate.current = row?.startDate;
    originalEndDate.current = row?.endDate;
    setInteracting(true);

    const onMouseMove = (ev) => {
      if (!isResizingRight?.current) return;
      const delta = ev?.clientX - dragStartX?.current;
      setResizeRightOffset(delta);
    };

    const onMouseUp = (ev) => {
      if (!isResizingRight?.current) return;
      isResizingRight.current = false;
      setInteracting(false);
      const delta = ev?.clientX - dragStartX?.current;
      const daysDelta = Math.round(delta / pixelsPerDay);
      if (daysDelta !== 0 && originalStartDate?.current && originalEndDate?.current) {
        const startD = parseLocalDate(originalStartDate?.current);
        const newEnd = addDays(parseLocalDate(originalEndDate?.current), daysDelta);
        // Don't allow end to go before start
        if (newEnd > startD) {
          onDateChange(row?.id, format(startD, 'yyyy-MM-dd'), format(newEnd, 'yyyy-MM-dd'));
        }
      }
      setResizeRightOffset(0);
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };

    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
  };

  const baseLeft = parseInt(barStyle?.left || 0);
  const baseWidth = parseInt(barStyle?.width || 0);

  // Apply offsets for visual feedback
  const left = baseLeft + (isDragging?.current ? dragOffset : 0) + (isResizingLeft?.current ? resizeLeftOffset : 0);
  const width = Math.max(
    pixelsPerDay,
    baseWidth
      - (isResizingLeft?.current ? resizeLeftOffset : 0)
      + (isResizingRight?.current ? resizeRightOffset : 0)
  );

  const barColorClass = isResource ? 'bg-indigo-400 opacity-80' : colors?.bar;

  return (
    <div
      className={`absolute rounded flex items-center text-white text-xs font-medium shadow-sm group/bar transition-all ${barColorClass} ${
        isPhase ? 'cursor-default' : 'cursor-move hover:brightness-110 hover:scale-y-105'
      } ${interacting ? 'opacity-70 shadow-lg z-10' : ''} ${isSaving ? 'opacity-60' : ''}`}
      style={{ left: `${left}px`, width: `${width}px`, top: '4px', height: '20px' }}
      onMouseDown={handleMouseDown}
      onMouseEnter={(e) => !interacting && onBarMouseEnter(e, row)}
      onMouseMove={(e) => !interacting && onBarMouseMove(e)}
      onMouseLeave={() => { if (!interacting) onBarMouseLeave(); }}
      onClick={(e) => { if (!isPhase && !interacting) onBarClick(e, row); }}
    >
      {/* Left resize handle */}
      {!isPhase && !isResource && (
        <div
          data-resize="left"
          onMouseDown={handleResizeLeftMouseDown}
          className="absolute left-0 top-0 bottom-0 w-2 cursor-w-resize flex items-center justify-center opacity-0 group-hover/bar:opacity-100 hover:bg-black/20 rounded-l z-10"
          title="Drag to change start date"
        >
          <div className="w-0.5 h-3 bg-white/80 rounded" />
        </div>
      )}

      {width > 40 && (
        <span className="truncate px-2" style={{ fontSize: '10px' }}>{row?.name}</span>
      )}

      {!isPhase && !isResource && (
        <div className="ml-auto flex-shrink-0 opacity-0 group-hover/bar:opacity-100 transition-opacity pr-1">
          {isSaving ? <Icon name="Loader2" size={9} className="animate-spin" /> : null}
        </div>
      )}

      {/* Right resize handle */}
      {!isPhase && !isResource && (
        <div
          data-resize="right"
          onMouseDown={handleResizeRightMouseDown}
          className="absolute right-0 top-0 bottom-0 w-2 cursor-e-resize flex items-center justify-center opacity-0 group-hover/bar:opacity-100 hover:bg-black/20 rounded-r z-10"
          title="Drag to change end date"
        >
          <div className="w-0.5 h-3 bg-white/80 rounded" />
        </div>
      )}
    </div>
  );
};

// ─── Build flat row list ──────────────────────────────────────────────────────
const buildRows = (deliverables, phases, expandedIds, filterResource, filterStatus, filterType, sortBy) => {
  const tasks = deliverables?.filter(d => d?.rowType === 'task');
  const subtasks = deliverables?.filter(d => d?.rowType === 'subtask');
  const topLevel = deliverables?.filter(d => !d?.rowType || d?.rowType === 'deliverable');

  const rows = [];

  const sortItems = (items) => {
    const sorted = [...items];
    switch (sortBy) {
      case 'name_asc': return sorted?.sort((a, b) => (a?.name || '')?.localeCompare(b?.name || ''));
      case 'name_desc': return sorted?.sort((a, b) => (b?.name || '')?.localeCompare(a?.name || ''));
      case 'start_asc': return sorted?.sort((a, b) => parseLocalDate(a?.startDate) - parseLocalDate(b?.startDate));
      case 'start_desc': return sorted?.sort((a, b) => parseLocalDate(b?.startDate) - parseLocalDate(a?.startDate));
      case 'status': return sorted?.sort((a, b) => (a?.status || '')?.localeCompare(b?.status || ''));
      default: return sorted;
    }
  };

  const itemMatchesFilters = (item) => {
    if (filterStatus && item?.status !== filterStatus) return false;
    if (filterType && item?.rowType !== filterType && !(filterType === 'deliverable' && !item?.rowType)) return false;
    return true;
  };

  const resourceMatchesFilter = (res) => {
    if (filterResource && res?.name !== filterResource) return false;
    return true;
  };

  const addItemRows = (item, level) => {
    const itemResources = (item?.assignedResources || [])?.filter(resourceMatchesFilter);
    const hasResources = itemResources?.length > 0;

    if (filterResource && !hasResources) return;
    if (!itemMatchesFilters(item) && !filterResource) return;

    const isExpanded = expandedIds?.has(item?.id);
    const childTasks = tasks?.filter(t => t?.parentId === item?.id);
    const hasChildren = childTasks?.length > 0;

    rows?.push({
      ...item,
      _kind: 'item',
      _level: level,
      _hasResources: hasResources,
      _isExpanded: isExpanded,
      _resourceCount: itemResources?.length,
      _hasChildren: hasChildren,
      _childType: 'task',
    });
  };

  const addDeliverableTree = (del, level) => {
    addItemRows(del, level);
    if (expandedIds?.has(del?.id)) {
      const delTasks = sortItems(tasks?.filter(t => t?.parentId === del?.id));
      delTasks?.forEach(task => {
        addItemRows(task, level + 1);
        if (expandedIds?.has(task?.id)) {
          const taskSubs = sortItems(subtasks?.filter(st => st?.parentId === task?.id));
          taskSubs?.forEach(sub => addItemRows(sub, level + 2));
        }
      });
    }
  };

  if (phases?.length > 0) {
    phases?.forEach(phase => {
      const phaseDeliverables = sortItems(topLevel?.filter(d => d?.phaseId === phase?.id));
      const hasChildren = phaseDeliverables?.length > 0;
      if (!filterResource && !filterStatus && !filterType) {
        rows?.push({
          ...phase,
          _kind: 'phase',
          rowType: 'phase',
          _level: 0,
          _hasResources: false,
          _isExpanded: expandedIds?.has(phase?.id),
          _hasChildren: hasChildren,
          _resourceCount: 0,
          startDate: phaseDeliverables?.[0]?.startDate,
          endDate: phaseDeliverables?.[phaseDeliverables?.length - 1]?.endDate,
        });
      }
      if (expandedIds?.has(phase?.id) || filterResource || filterStatus || filterType) {
        phaseDeliverables?.forEach(del => addDeliverableTree(del, filterResource || filterStatus || filterType ? 0 : 1));
      }
    });
    const unphased = sortItems(topLevel?.filter(d => !d?.phaseId || !phases?.find(p => p?.id === d?.phaseId)));
    if (unphased?.length > 0) {
      if (!filterResource && !filterStatus && !filterType) {
        rows?.push({
          id: '__unphased__',
          name: 'Unassigned',
          _kind: 'phase',
          rowType: 'phase',
          _level: 0,
          _hasResources: false,
          _isExpanded: expandedIds?.has('__unphased__'),
          _hasChildren: true,
          _resourceCount: 0,
          startDate: unphased?.[0]?.startDate,
          endDate: unphased?.[unphased?.length - 1]?.endDate,
        });
      }
      if (expandedIds?.has('__unphased__') || filterResource || filterStatus || filterType) {
        unphased?.forEach(del => addDeliverableTree(del, filterResource || filterStatus || filterType ? 0 : 1));
      }
    }
  } else {
    sortItems(topLevel)?.forEach(del => addDeliverableTree(del, 0));
  }

  return rows;
};

// ─── Build resource utilization rows ─────────────────────────────────────────
const buildResourceUtilizationRows = (deliverables, filterResource) => {
  const resourceMap = new Map();

  deliverables?.forEach(item => {
    (item?.assignedResources || [])?.forEach((res) => {
      const name = res?.name || res?.resourceName || 'Unknown Resource';
      if (filterResource && name !== filterResource) return;

      if (!resourceMap?.has(name)) {
        resourceMap?.set(name, {
          id: `__res__${name}`,
          _kind: 'resource',
          _level: 0,
          name,
          category: res?.category || res?.resourceCategory || '',
          rowType: 'resource',
          _hasResources: false,
          _isExpanded: false,
          _resourceCount: 0,
          _allocations: [],
        });
      }

      const entry = resourceMap?.get(name);
      const startDate = res?.allocationStartDate || item?.startDate;
      const endDate = res?.allocationEndDate || item?.endDate;
      const hours = res?.allocationHours || res?.hours || null;

      entry?._allocations?.push({
        startDate,
        endDate,
        hours,
        parentName: item?.name,
        status: item?.status,
      });

      if (!entry?.startDate || (startDate && startDate < entry?.startDate)) entry.startDate = startDate;
      if (!entry?.endDate || (endDate && endDate > entry?.endDate)) entry.endDate = endDate;
      if (hours) entry._allocationHours = (entry?._allocationHours || 0) + hours;
    });
  });

  const result = Array.from(resourceMap?.values())?.sort((a, b) => a?.name?.localeCompare(b?.name));
  result?.forEach(resource => {
    const allocs = resource?._allocations || [];
    let conflictCount = 0;
    for (let i = 0; i < allocs?.length; i++) {
      for (let j = i + 1; j < allocs?.length; j++) {
        const aStart = allocs?.[i]?.startDate ? parseLocalDate(allocs?.[i]?.startDate) : null;
        const aEnd = allocs?.[i]?.endDate ? parseLocalDate(allocs?.[i]?.endDate) : null;
        const bStart = allocs?.[j]?.startDate ? parseLocalDate(allocs?.[j]?.startDate) : null;
        const bEnd = allocs?.[j]?.endDate ? parseLocalDate(allocs?.[j]?.endDate) : null;
        if (aStart && aEnd && bStart && bEnd) {
          if (aStart <= bEnd && bStart <= aEnd) conflictCount++;
        }
      }
    }
    resource._conflictCount = conflictCount;
    resource._hasConflict = conflictCount > 0;
  });

  return result;
};

// ─── Main Component ───────────────────────────────────────────────────────────
const ResourceGanttView = ({ deliverables, phases, onEdit, onDateChange, onUpdate, onAddChild, onAddPhase, onEditPhase, onReorder, project, onAdjustDates }) => {
  const projectId = project?.id || 'default';

  const [expandedIds, setExpandedIds] = useState(() => {
    try {
      const saved = localStorage.getItem(`res_gantt_expanded_ids_${projectId}`);
      return saved ? new Set(JSON.parse(saved)) : new Set();
    } catch { return new Set(); }
  });
  const [filterResource, setFilterResource] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterType, setFilterType] = useState('');
  const [sortBy, setSortBy] = useState('name_asc');
  const [timeScale, setTimeScale] = useState(() => {
    return localStorage.getItem('gantt_time_scale') || 'weekly';
  });
  const [pixelsPerDay, setPixelsPerDay] = useState(() => {
    const saved = localStorage.getItem('gantt_time_scale') || 'weekly';
    return TIME_SCALE_PIXELS?.[saved] || 20;
  });
  const [showFilters, setShowFilters] = useState(false);
  const [showColumnPicker, setShowColumnPicker] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState(() => {
    try {
      const saved = localStorage.getItem(`res_gantt_visible_cols_${projectId}`);
      return saved ? JSON.parse(saved) : ['startDate', 'endDate', 'status'];
    } catch { return ['startDate', 'endDate', 'status']; }
  });
  const [columnWidths, setColumnWidths] = useState(() => {
    try {
      const saved = localStorage.getItem(`res_gantt_col_widths_${projectId}`);
      if (saved) return JSON.parse(saved);
    } catch {}
    const w = {};
    ALL_COLUMNS?.forEach(c => { w[c.id] = c?.defaultWidth; });
    return w;
  });
  const [nameColWidth, setNameColWidth] = useState(() => {
    try {
      const saved = localStorage.getItem(`res_gantt_name_col_width_${projectId}`);
      return saved ? Number(saved) : DEFAULT_NAME_WIDTH;
    } catch { return DEFAULT_NAME_WIDTH; }
  });
  const [tooltip, setTooltip] = useState({ visible: false, row: null, x: 0, y: 0 });
  const [dateEditor, setDateEditor] = useState({ visible: false, row: null, x: 0, y: 0 });
  const [savingDateId, setSavingDateId] = useState(null);
  const [savingStatusId, setSavingStatusId] = useState(null);
  const [savingNameId, setSavingNameId] = useState(null);
  const [resourceSectionCollapsed, setResourceSectionCollapsed] = useState(false);
  // Inline editing state
  const [editingNameId, setEditingNameId] = useState(null);
  const [statusDropdownId, setStatusDropdownId] = useState(null);
  const [dragOverRowId, setDragOverRowId] = useState(null);
  const rowDragItemRef = useRef(null);

  const containerRef = useRef(null);
  const wrapperRef = useRef(null);
  const leftPanelRef = useRef(null);
  const rightPanelRef = useRef(null);
  const resLeftPanelRef = useRef(null);
  const resRightPanelRef = useRef(null);
  const isSyncingScroll = useRef(false);
  const isSyncingResScroll = useRef(false);

  const syncScroll = (source, target) => {
    if (isSyncingScroll?.current) return;
    isSyncingScroll.current = true;
    if (target?.current) target.current.scrollTop = source?.current?.scrollTop;
    isSyncingScroll.current = false;
  };

  const syncResScroll = (source, target) => {
    if (isSyncingResScroll?.current) return;
    isSyncingResScroll.current = true;
    if (target?.current) target.current.scrollTop = source?.current?.scrollTop;
    isSyncingResScroll.current = false;
  };

  // Sync horizontal scroll between main and resource sections
  const syncHorizontalScroll = (sourceRef, targetRef) => {
    if (targetRef?.current && sourceRef?.current) {
      targetRef.current.scrollLeft = sourceRef?.current?.scrollLeft;
    }
  };

  // Dynamic sizing: observe wrapper
  useEffect(() => {
    if (!wrapperRef?.current) return;
    const observer = new ResizeObserver(() => {});
    observer?.observe(wrapperRef?.current);
    return () => observer?.disconnect();
  }, []);

  // Persist time scale to localStorage
  useEffect(() => {
    localStorage.setItem('gantt_time_scale', timeScale);
    setPixelsPerDay(TIME_SCALE_PIXELS?.[timeScale] || 20);
  }, [timeScale]);

  // Persist expansion, column visibility, widths
  useEffect(() => {
    localStorage.setItem(`res_gantt_expanded_ids_${projectId}`, JSON.stringify(Array.from(expandedIds)));
  }, [expandedIds, projectId]);

  useEffect(() => {
    localStorage.setItem(`res_gantt_visible_cols_${projectId}`, JSON.stringify(visibleColumns));
  }, [visibleColumns, projectId]);

  useEffect(() => {
    localStorage.setItem(`res_gantt_col_widths_${projectId}`, JSON.stringify(columnWidths));
  }, [columnWidths, projectId]);

  useEffect(() => {
    localStorage.setItem(`res_gantt_name_col_width_${projectId}`, String(nameColWidth));
  }, [nameColWidth, projectId]);

  const allResourceNames = useMemo(() => {
    const names = new Set();
    deliverables?.forEach(d => {
      d?.assignedResources?.forEach(r => {
        if (r?.name || r?.resourceName) names?.add(r?.name || r?.resourceName);
      });
    });
    return Array.from(names)?.sort();
  }, [deliverables]);

  const rows = useMemo(() =>
    buildRows(deliverables, phases, expandedIds, filterResource, filterStatus, filterType, sortBy),
    [deliverables, phases, expandedIds, filterResource, filterStatus, filterType, sortBy]
  );

  const resourceRows = useMemo(() =>
    buildResourceUtilizationRows(deliverables, filterResource),
    [deliverables, filterResource]
  );

  const allDates = useMemo(() => {
    const dates = deliverables?.flatMap(d => [
      d?.startDate ? parseLocalDate(d?.startDate) : null,
      d?.endDate ? parseLocalDate(d?.endDate) : null,
      ...(d?.assignedResources || [])?.flatMap(r => [
        r?.allocationStartDate ? parseLocalDate(r?.allocationStartDate) : null,
        r?.allocationEndDate ? parseLocalDate(r?.allocationEndDate) : null,
      ])
    ])?.filter(Boolean);
    return dates;
  }, [deliverables]);

  if (!deliverables?.length) {
    return (
      <div className="bg-card rounded-lg border border-border p-12 text-center">
        <Icon name="Users" size={48} className="mx-auto mb-4 text-muted-foreground" />
        <h3 className="text-lg font-semibold text-foreground mb-2">No Items to Display</h3>
        <p className="text-sm text-muted-foreground">Add deliverables with resource allocations to see the Resource Gantt.</p>
      </div>
    );
  }

  if (!allDates?.length) {
    return (
      <div className="bg-card rounded-lg border border-border p-12 text-center">
        <Icon name="Calendar" size={48} className="mx-auto mb-4 text-muted-foreground" />
        <h3 className="text-lg font-semibold text-foreground mb-2">No Date Information</h3>
        <p className="text-sm text-muted-foreground">Deliverables need start and end dates to display in the Resource Gantt.</p>
      </div>
    );
  }

  const minDate = startOfMonth(new Date(Math.min(...allDates)));
  const maxDate = endOfMonth(new Date(Math.max(...allDates)));
  const days = eachDayOfInterval({ start: minDate, end: maxDate });

  // Build time scale headers
  const buildTimeScaleHeaders = () => {
    if (timeScale === 'daily') {
      const monthGroups = [];
      let curMonth = null, curCount = 0;
      days?.forEach((day, i) => {
        const key = format(day, 'MMM yyyy');
        if (key !== curMonth) {
          if (curMonth) monthGroups?.push({ label: curMonth, count: curCount });
          curMonth = key; curCount = 1;
        } else { curCount++; }
        if (i === days?.length - 1) monthGroups?.push({ label: curMonth, count: curCount });
      });
      return {
        topGroups: monthGroups,
        bottomUnits: days?.map(day => ({
          label: format(day, 'd'),
          width: pixelsPerDay,
          isWeekend: day?.getDay() === 0 || day?.getDay() === 6,
          date: day,
        })),
      };
    }
    if (timeScale === 'weekly') {
      const weeks = eachWeekOfInterval({ start: minDate, end: maxDate }, { weekStartsOn: 1 });
      const monthGroups = [];
      let curMonth = null, curCount = 0;
      weeks?.forEach((weekStart, i) => {
        const key = format(weekStart, 'MMM yyyy');
        if (key !== curMonth) {
          if (curMonth) monthGroups?.push({ label: curMonth, count: curCount });
          curMonth = key; curCount = 1;
        } else { curCount++; }
        if (i === weeks?.length - 1) monthGroups?.push({ label: curMonth, count: curCount });
      });
      const weekWidthPx = pixelsPerDay * 7;
      return {
        topGroups: monthGroups,
        bottomUnits: weeks?.map(weekStart => {
          const weekEnd = addDays(weekStart, 6);
          const startMonth = format(weekStart, 'MMM');
          const endMonth = format(weekEnd, 'MMM');
          const label = startMonth === endMonth
            ? `${startMonth} ${format(weekStart, 'd')} - ${format(weekEnd, 'd')}`
            : `${startMonth} ${format(weekStart, 'd')} - ${endMonth} ${format(weekEnd, 'd')}`;
          return {
            label,
            width: weekWidthPx,
            isWeekend: false,
            date: weekStart,
          };
        }),
      };
    }
    if (timeScale === 'monthly') {
      const months = eachMonthOfInterval({ start: minDate, end: maxDate });
      const yearGroups = [];
      let curYear = null, curCount = 0;
      months?.forEach((month, i) => {
        const key = format(month, 'yyyy');
        if (key !== curYear) {
          if (curYear) yearGroups?.push({ label: curYear, count: curCount });
          curYear = key; curCount = 1;
        } else { curCount++; }
        if (i === months?.length - 1) yearGroups?.push({ label: curYear, count: curCount });
      });
      return {
        topGroups: yearGroups,
        bottomUnits: months?.map(month => {
          const daysInMonth = differenceInDays(endOfMonth(month), month) + 1;
          return { label: format(month, 'MMM'), width: daysInMonth * pixelsPerDay, isWeekend: false, date: month };
        }),
      };
    }
    if (timeScale === 'quarterly') {
      const quarters = [];
      let cur = startOfQuarter(minDate);
      while (cur <= maxDate) { quarters?.push(cur); cur = startOfQuarter(addQuarters(cur, 1)); }
      const yearGroups = [];
      let curYear = null, curCount = 0;
      quarters?.forEach((q, i) => {
        const key = format(q, 'yyyy');
        if (key !== curYear) {
          if (curYear) yearGroups?.push({ label: curYear, count: curCount });
          curYear = key; curCount = 1;
        } else { curCount++; }
        if (i === quarters?.length - 1) yearGroups?.push({ label: curYear, count: curCount });
      });
      return {
        topGroups: yearGroups,
        bottomUnits: quarters?.map(q => {
          const daysInQ = differenceInDays(endOfQuarter(q), q) + 1;
          return { label: `Q${Math.ceil((q?.getMonth() + 1) / 3)} ${format(q, 'yyyy')}`, width: daysInQ * pixelsPerDay, isWeekend: false, date: q };
        }),
      };
    }
    return { topGroups: [], bottomUnits: [] };
  };

  const timeScaleHeaders = buildTimeScaleHeaders();
  const totalTimelineWidth = timeScaleHeaders?.bottomUnits?.reduce((sum, u) => sum + u?.width, 0) || days?.length * pixelsPerDay;

  const monthGroups = timeScaleHeaders?.topGroups;

  const toggleExpand = (id) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next?.has(id)) next?.delete(id); else next?.add(id);
      return next;
    });
  };

  const expandAll = () => {
    const ids = new Set();
    deliverables?.forEach(d => { ids?.add(d?.id); });
    phases?.forEach(p => ids?.add(p?.id));
    ids?.add('__unphased__');
    setExpandedIds(ids);
  };

  const collapseAll = () => setExpandedIds(new Set());

  const clearFilters = () => {
    setFilterResource('');
    setFilterStatus('');
    setFilterType('');
  };

  const hasActiveFilters = filterResource || filterStatus || filterType;

  const toggleColumn = (colId) => {
    setVisibleColumns(prev =>
      prev?.includes(colId) ? prev?.filter(c => c !== colId) : [...prev, colId]
    );
  };

  const handleColResize = (colId, delta, minW) => {
    setColumnWidths(prev => ({
      ...prev,
      [colId]: Math.max(minW, (prev?.[colId] || 80) + delta)
    }));
  };

  const handleNameColResize = (delta) => {
    setNameColWidth(prev => Math.max(120, prev + delta));
  };

  const colW = (id) => columnWidths?.[id] || ALL_COLUMNS?.find(c => c?.id === id)?.defaultWidth || 80;
  const colVisible = (id) => visibleColumns?.includes(id);

  const getBarStyle = (row) => {
    if (!row?.startDate || !row?.endDate) return null;
    const startDiff = differenceInDays(parseLocalDate(row?.startDate), minDate);
    const duration = Math.max(differenceInDays(parseLocalDate(row?.endDate), parseLocalDate(row?.startDate)) + 1, 1);
    return {
      left: `${startDiff * pixelsPerDay}px`,
      width: `${duration * pixelsPerDay}px`,
    };
  };

  const getBarStyleForDates = (startDate, endDate) => {
    if (!startDate || !endDate) return null;
    const startDiff = differenceInDays(parseLocalDate(startDate), minDate);
    const duration = Math.max(differenceInDays(parseLocalDate(endDate), parseLocalDate(startDate)) + 1, 1);
    return {
      left: `${startDiff * pixelsPerDay}px`,
      width: `${duration * pixelsPerDay}px`,
    };
  };

  // Total left panel width
  const fixedColsWidth = ALL_COLUMNS
    ?.filter(c => visibleColumns?.includes(c?.id))
    ?.reduce((sum, c) => sum + colW(c?.id), 0);
  const totalFixedWidth = nameColWidth + fixedColsWidth;

  const ROW_HEIGHT = 28;
  const HEADER_HEIGHT = 44;
  const RES_ROW_HEIGHT = 28;

  const handleBarMouseEnter = (e, row) => {
    setTooltip({ visible: true, row, x: e?.clientX, y: e?.clientY });
  };

  const handleBarMouseMove = (e) => {
    setTooltip(prev => prev?.visible ? { ...prev, x: e?.clientX, y: e?.clientY } : prev);
  };

  const handleBarMouseLeave = () => {
    setTooltip({ visible: false, row: null, x: 0, y: 0 });
  };

  const handleBarClick = (e, row) => {
    if (row?._kind === 'phase') return;
    e?.stopPropagation();
    setTooltip({ visible: false, row: null, x: 0, y: 0 });
    setDateEditor({ visible: true, row, x: e?.clientX, y: e?.clientY + 10 });
  };

  const handleDateSave = async (startDate, endDate) => {
    if (!dateEditor?.row) return;
    const row = dateEditor?.row;
    setSavingDateId(row?.id);
    try {
      // For resource rows, we update the parent item's dates
      const targetId = row?._kind === 'resource' ? row?._parentId : row?.id;
      if (targetId && onDateChange) {
        await onDateChange(targetId, startDate, endDate);
      }
      setDateEditor({ visible: false, row: null, x: 0, y: 0 });
    } finally {
      setSavingDateId(null);
    }
  };

  const handleDragDateChange = async (rowId, startDate, endDate) => {
    if (!onDateChange) return;
    setSavingDateId(rowId);
    try {
      await onDateChange(rowId, startDate, endDate);
    } finally {
      setSavingDateId(null);
    }
  };

  // ── Inline name save ──
  const handleNameSave = async (row, newName) => {
    if (!onUpdate) return;
    setSavingNameId(row?.id);
    try {
      await onUpdate(row?.id, { name: newName });
    } finally {
      setSavingNameId(null);
      setEditingNameId(null);
    }
  };

  // ── Inline status save ──
  const handleStatusSave = async (row, newStatus) => {
    if (!onUpdate) return;
    setSavingStatusId(row?.id);
    try {
      await onUpdate(row?.id, { status: newStatus });
    } finally {
      setSavingStatusId(null);
    }
  };

  // ── Inline date cell save ──
  const handleInlineDateSave = async (row, field, value) => {
    if (!onUpdate) return;
    setSavingDateId(row?.id);
    try {
      await onUpdate(row?.id, { [field]: value });
    } finally {
      setSavingDateId(null);
    }
  };

  // ── Row reorder helpers ──
  const sortItemsForReorder = (items, type) => {
    return [...items]?.sort((a, b) => {
      const ao = a?.customOrder ?? Infinity;
      const bo = b?.customOrder ?? Infinity;
      if (ao !== bo) return ao - bo;
      return (a?.sortOrder ?? 0) - (b?.sortOrder ?? 0);
    });
  };

  const handleRowDragStart = (e, row) => {
    if (row?._kind === 'phase' || row?._kind === 'resource') { e?.preventDefault(); return; }
    rowDragItemRef.current = row;
    e?.dataTransfer?.setData('text/plain', row?.id);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleRowDragOver = (e, row) => {
    if (row?._kind === 'phase' || row?._kind === 'resource') return;
    e?.preventDefault();
    setDragOverRowId(row?.id);
  };

  const handleRowDrop = async (e, targetRow) => {
    e?.preventDefault();
    setDragOverRowId(null);
    const draggedRow = rowDragItemRef?.current;
    if (!draggedRow || draggedRow?.id === targetRow?.id) return;
    if (targetRow?._kind === 'phase' || targetRow?._kind === 'resource') return;

    const draggedType = draggedRow?.rowType || 'deliverable';
    const targetType = targetRow?.rowType || 'deliverable';
    if (draggedType !== targetType) return;
    if (draggedRow?.parentId !== targetRow?.parentId) return;

    const siblings = sortItemsForReorder(deliverables?.filter(d => {
      const dt = d?.rowType || 'deliverable';
      return dt === draggedType && d?.parentId === draggedRow?.parentId;
    }));

    const dragIdx = siblings?.findIndex(s => s?.id === draggedRow?.id);
    const targetIdx = siblings?.findIndex(s => s?.id === targetRow?.id);
    if (dragIdx === -1 || targetIdx === -1) return;

    const reordered = [...siblings];
    reordered?.splice(dragIdx, 1);
    reordered?.splice(targetIdx, 0, draggedRow);

    const updates = reordered?.map((item, idx) => {
      let newOrder;
      if (draggedType === 'deliverable') {
        newOrder = idx + 1;
      } else if (draggedType === 'task') {
        const parent = deliverables?.find(d => d?.id === item?.parentId);
        const parentOrder = parent?.customOrder != null ? Math.floor(Number(parent?.customOrder)) : 1;
        newOrder = parseFloat(`${parentOrder}.${idx + 1}`);
      } else if (draggedType === 'subtask') {
        const parentTask = deliverables?.find(d => d?.id === item?.parentId);
        const parentOrder = parentTask?.customOrder != null ? Number(parentTask?.customOrder) : 1.1;
        const parentStr = parentOrder?.toFixed(1);
        newOrder = parseFloat(`${parentStr}${idx + 1}`);
      }
      return { id: item?.id, customOrder: newOrder };
    });

    await onReorder?.(updates);
    rowDragItemRef.current = null;
  };

  const handleRowDragEnd = () => {
    setDragOverRowId(null);
    rowDragItemRef.current = null;
  };

  // Render a single left-panel row (shared between main and resource sections)
  const renderLeftRow = (row, isResourceSection = false) => {
    const isPhase = row?._kind === 'phase';
    const isResource = row?._kind === 'resource';
    const rowType = row?.rowType || 'deliverable';
    const colors = ROW_TYPE_COLORS?.[rowType] || ROW_TYPE_COLORS?.deliverable;
    const indentPx = row?._level * 16;
    const statusBadge = getStatusBadge(row?.status);
    const isEditingName = editingNameId === row?.id;
    const isStatusOpen = statusDropdownId === row?.id;
    const isSavingName = savingNameId === row?.id;
    const isSavingStatus = savingStatusId === row?.id;
    const isSavingDate = savingDateId === row?.id;
    const isDraggable = !isPhase && !isResource;
    const isDragOver = dragOverRowId === row?.id;

    const rowBg = isPhase
      ? 'bg-amber-50/60 hover:bg-amber-50/80'
      : isResource
      ? (row?._hasConflict ? 'bg-red-50/40 hover:bg-red-50/60' : 'bg-indigo-50/30 hover:bg-indigo-50/60')
      : colors?.bg + ' hover:brightness-95';

    return (
      <div
        key={row?.id}
        className={`flex border-b border-border group ${rowBg} ${isDragOver ? 'border-t-2 border-t-primary' : ''}`}
        style={{ height: `${isResourceSection ? RES_ROW_HEIGHT : ROW_HEIGHT}px` }}
        draggable={isDraggable}
        onDragStart={isDraggable ? (e) => handleRowDragStart(e, row) : undefined}
        onDragOver={isDraggable ? (e) => handleRowDragOver(e, row) : undefined}
        onDrop={isDraggable ? (e) => handleRowDrop(e, row) : undefined}
        onDragEnd={isDraggable ? handleRowDragEnd : undefined}
      >
        {/* Name col */}
        <div
          className="flex-shrink-0 flex items-center border-r border-border bg-inherit gap-1 pr-1"
          style={{ width: `${nameColWidth}px`, paddingLeft: `${6 + indentPx}px` }}
        >
          {/* Drag handle */}
          {isDraggable && (
            <span className="flex-shrink-0 opacity-0 group-hover:opacity-60 cursor-grab active:cursor-grabbing text-muted-foreground mr-0.5" title="Drag to reorder">
              <Icon name="GripVertical" size={11} />
            </span>
          )}
          {row?._hasResources || row?._hasChildren ? (
            <button
              onClick={() => toggleExpand(row?.id)}
              className="flex-shrink-0 w-4 h-4 flex items-center justify-center rounded hover:bg-muted/60 text-muted-foreground"
            >
              <Icon name={row?._isExpanded ? 'ChevronDown' : 'ChevronRight'} size={11} />
            </button>
          ) : (
            <span className="flex-shrink-0 w-4" />
          )}
          <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${
            isPhase ? 'bg-amber-500' : isResource ? 'bg-indigo-400' : colors?.dot
          }`} />

          {/* Inline name editor or static name */}
          {!isPhase && !isResource && isEditingName ? (
            <InlineNameEditor
              value={row?.name}
              onSave={(newName) => handleNameSave(row, newName)}
              onCancel={() => setEditingNameId(null)}
            />
          ) : (
            <span
              className={`text-xs truncate font-medium flex-1 min-w-0 ${
                isPhase ? 'text-amber-800' : isResource ? 'text-indigo-700' : 'text-foreground'
              } ${!isPhase && !isResource ? 'cursor-text' : ''}`}
              onDoubleClick={!isPhase && !isResource ? () => setEditingNameId(row?.id) : undefined}
              title={!isPhase && !isResource ? 'Double-click to rename' : undefined}
            >
              {row?.name}
            </span>
          )}

          {isSavingName && <Icon name="Loader2" size={10} className="animate-spin text-muted-foreground flex-shrink-0" />}

          {isResource && row?.category && !isEditingName && (
            <span className="flex-shrink-0 ml-1 text-xs text-muted-foreground truncate" style={{ fontSize: '10px' }}>
              {row?.category}
            </span>
          )}
          {isResource && row?._hasConflict && (
            <span
              className="flex-shrink-0 ml-1 flex items-center gap-0.5 bg-red-100 text-red-700 border border-red-300 rounded-full px-1.5 py-0 leading-4 font-semibold"
              style={{ fontSize: '10px' }}
              title={`${row?._conflictCount} scheduling conflict${row?._conflictCount !== 1 ? 's' : ''} — resource exceeds capacity`}
            >
              <Icon name="AlertTriangle" size={9} className="text-red-600" />
              {row?._conflictCount}
            </span>
          )}
          {!isPhase && !isResource && row?._resourceCount > 0 && !isEditingName && (
            <span className="flex-shrink-0 ml-auto mr-1 text-xs bg-indigo-100 text-indigo-700 rounded-full px-1 py-0 leading-4" style={{ fontSize: '10px' }}>
              {row?._resourceCount}
            </span>
          )}
          {/* Add child button (hover) */}
          {!isPhase && !isResource && onAddChild && !isEditingName && (
            <button
              onClick={(e) => { e?.stopPropagation(); onAddChild(row, row?.rowType === 'task' ? 'subtask' : 'task'); }}
              className="flex-shrink-0 opacity-0 group-hover:opacity-100 p-0.5 rounded hover:bg-primary/10 text-primary transition-opacity"
              title={row?.rowType === 'task' ? 'Add Sub-task' : 'Add Task'}
            >
              <Icon name="Plus" size={10} />
            </button>
          )}
          {!isPhase && !isResource && onEdit && !isEditingName && (
            <button
              onClick={() => onEdit(row)}
              className="flex-shrink-0 opacity-0 group-hover:opacity-100 p-0.5 rounded hover:bg-muted/60 text-muted-foreground hover:text-foreground transition-opacity"
            >
              <Icon name="Edit2" size={10} />
            </button>
          )}
          {/* Phase action buttons */}
          {isPhase && row?.id !== '__unphased__' && (
            <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0 ml-auto mr-1">
              <button
                onClick={(e) => { e?.stopPropagation(); onAddPhase && onAddPhase(); }}
                title="Add new phase"
                className="p-0.5 rounded hover:bg-amber-200 text-amber-700 transition-colors"
              >
                <Icon name="Plus" size={10} />
              </button>
              <button
                onClick={(e) => { e?.stopPropagation(); onEditPhase && onEditPhase(row); }}
                title="Edit phase"
                className="p-0.5 rounded hover:bg-amber-200 text-amber-700 transition-colors"
              >
                <Icon name="Pencil" size={10} />
              </button>
            </div>
          )}
        </div>
        {/* Configurable columns */}
        {colVisible('resource') && (
          <div className="flex-shrink-0 flex items-center border-r border-border px-2 gap-1" style={{ width: `${colW('resource')}px` }}>
            {isResource ? (
              <div className="flex items-center gap-1">
                <div className="w-4 h-4 rounded-full bg-indigo-500 text-white flex items-center justify-center flex-shrink-0" style={{ fontSize: '9px' }}>
                  {row?.name?.charAt(0)?.toUpperCase()}
                </div>
                <span className="text-xs text-indigo-700 truncate">{row?.name}</span>
              </div>
            ) : !isPhase && row?._resourceCount > 0 ? (
              <div className="flex items-center gap-0.5">
                {(deliverables?.find(d => d?.id === row?.id)?.assignedResources || [])?.slice(0, 3)?.map((r, i) => (
                  <div key={i} className="w-4 h-4 rounded-full bg-indigo-500 text-white flex items-center justify-center flex-shrink-0" title={r?.name} style={{ fontSize: '9px' }}>
                    {(r?.name || r?.resourceName || '?')?.charAt(0)?.toUpperCase()}
                  </div>
                ))}
                {row?._resourceCount > 3 && <span className="text-xs text-muted-foreground">+{row?._resourceCount - 3}</span>}
              </div>
            ) : (
              <span className="text-xs text-muted-foreground">—</span>
            )}
          </div>
        )}
        {colVisible('startDate') && (
          <div className="flex-shrink-0 flex items-center border-r border-border px-2" style={{ width: `${colW('startDate')}px` }}>
            {!isPhase && !isResource && onUpdate ? (
              <div className="relative w-full">
                {isSavingDate ? (
                  <Icon name="Loader2" size={10} className="animate-spin text-muted-foreground" />
                ) : (
                  <InlineDateCell
                    value={row?.startDate}
                    label="start date"
                    minDate={undefined}
                    onChange={(v) => handleInlineDateSave(row, 'startDate', v)}
                  />
                )}
              </div>
            ) : (
              <span className="text-xs text-muted-foreground">
                {row?.startDate ? format(parseLocalDate(row?.startDate), 'MMM d') : '—'}
              </span>
            )}
          </div>
        )}
        {colVisible('endDate') && (
          <div className="flex-shrink-0 flex items-center border-r border-border px-2" style={{ width: `${colW('endDate')}px` }}>
            {!isPhase && !isResource && onUpdate ? (
              <div className="relative w-full">
                {isSavingDate ? (
                  <Icon name="Loader2" size={10} className="animate-spin text-muted-foreground" />
                ) : (
                  <InlineDateCell
                    value={row?.endDate}
                    label="end date"
                    minDate={row?.startDate ? row?.startDate?.slice(0, 10) : undefined}
                    onChange={(v) => handleInlineDateSave(row, 'endDate', v)}
                  />
                )}
              </div>
            ) : (
              <span className="text-xs text-muted-foreground">
                {row?.endDate ? format(parseLocalDate(row?.endDate), 'MMM d') : '—'}
              </span>
            )}
          </div>
        )}
        {colVisible('status') && (
          <div className="flex-shrink-0 flex items-center border-r border-border px-1 relative" style={{ width: `${colW('status')}px` }}>
            {!isPhase && !isResource && onUpdate ? (
              <div className="relative">
                <button
                  onClick={() => setStatusDropdownId(isStatusOpen ? null : row?.id)}
                  className={`text-xs px-3 py-0 rounded cursor-pointer hover:brightness-95 transition-all flex items-center gap-1 ${statusBadge?.cls}`}
                  title="Click to change status"
                >
                  {isSavingStatus
                    ? <Icon name="Loader2" size={9} className="animate-spin" />
                    : statusBadge?.label
                  }
                </button>
                {isStatusOpen && (
                  <InlineStatusDropdown
                    currentStatus={row?.status}
                    onSave={(newStatus) => handleStatusSave(row, newStatus)}
                    onClose={() => setStatusDropdownId(null)}
                  />
                )}
              </div>
            ) : !isPhase && !isResource ? (
              <span className={`text-xs px-3 py-0 rounded ${statusBadge?.cls}`}>{statusBadge?.label}</span>
            ) : null}
          </div>
        )}
        {colVisible('hours') && (
          <div className="flex-shrink-0 flex items-center border-r border-border px-2" style={{ width: `${colW('hours')}px` }}>
            <span className="text-xs text-muted-foreground">
              {isResource && row?._allocationHours ? `${row?._allocationHours}h` : '—'}
            </span>
          </div>
        )}
      </div>
    );
  };

  // Render a single right-panel (timeline) row
  const renderRightRow = (row, isResourceSection = false) => {
    const isPhase = row?._kind === 'phase';
    const isResource = row?._kind === 'resource';
    const rowType = row?.rowType || 'deliverable';
    const colors = ROW_TYPE_COLORS?.[rowType] || ROW_TYPE_COLORS?.deliverable;
    const barStyle = getBarStyle(row);
    const rowHeight = isResourceSection ? RES_ROW_HEIGHT : ROW_HEIGHT;

    const rowBg = isPhase
      ? 'bg-amber-50/60 hover:bg-amber-50/80'
      : isResource
      ? 'bg-indigo-50/30 hover:bg-indigo-50/60' : colors?.bg + ' hover:brightness-95';

    return (
      <div key={row?.id} className={`flex border-b border-border ${rowBg}`} style={{ height: `${rowHeight}px`, width: `${days?.length * pixelsPerDay}px` }}>
        <div className="relative w-full" style={{ height: `${rowHeight}px` }}>
          {/* Weekend shading */}
          {days?.map((day, i) => {
            const isWeekend = day?.getDay() === 0 || day?.getDay() === 6;
            return isWeekend ? (
              <div key={i} className="absolute top-0 bottom-0 bg-muted/30" style={{ left: `${i * pixelsPerDay}px`, width: `${pixelsPerDay}px` }} />
            ) : null;
          })}

          {/* For resource rows: render one bar per allocation span */}
          {isResource && row?._allocations?.map((alloc, ai) => {
            const allocBarStyle = getBarStyleForDates(alloc?.startDate, alloc?.endDate);
            if (!allocBarStyle) return null;
            const left = parseInt(allocBarStyle?.left || 0);
            const width = parseInt(allocBarStyle?.width || 0);
            return (
              <div
                key={ai}
                className="absolute rounded bg-indigo-400 opacity-80 flex items-center px-1.5 text-white text-xs font-medium shadow-sm cursor-default hover:brightness-110"
                style={{ left: `${left}px`, width: `${width}px`, top: '4px', height: '20px' }}
                onMouseEnter={(e) => handleBarMouseEnter(e, { ...row, startDate: alloc?.startDate, endDate: alloc?.endDate, _allocationHours: alloc?.hours, _parentName: alloc?.parentName })}
                onMouseMove={handleBarMouseMove}
                onMouseLeave={handleBarMouseLeave}
              >
                {width > 50 && (
                  <span className="truncate" style={{ fontSize: '10px' }}>{alloc?.parentName || row?.name}</span>
                )}
              </div>
            );
          })}

          {/* For non-resource, non-phase rows: draggable bar */}
          {!isResource && barStyle && !isPhase && (
            <DraggableBar
              row={row}
              barStyle={barStyle}
              isPhase={false}
              isResource={false}
              colors={colors}
              pixelsPerDay={pixelsPerDay}
              minDate={minDate}
              onDateChange={handleDragDateChange}
              onBarMouseEnter={handleBarMouseEnter}
              onBarMouseMove={handleBarMouseMove}
              onBarMouseLeave={handleBarMouseLeave}
              onBarClick={handleBarClick}
              savingDateId={savingDateId}
            />
          )}

          {isPhase && row?.startDate && row?.endDate && (
            <div
              className="absolute bg-amber-300/40 border-t-2 border-amber-500 rounded"
              style={{ ...getBarStyle(row), top: '4px', height: '20px' }}
              onMouseEnter={(e) => handleBarMouseEnter(e, row)}
              onMouseMove={handleBarMouseMove}
              onMouseLeave={handleBarMouseLeave}
            />
          )}
        </div>
      </div>
    );
  };

  return (
    <div ref={wrapperRef} className="bg-card rounded-lg border border-border overflow-hidden w-full">
      {/* Toolbar */}
      <div className="px-3 py-2 border-b border-border flex flex-wrap items-center justify-between gap-2 bg-muted/20">
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={expandAll}
            className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground px-2 py-1 rounded hover:bg-muted border border-border"
          >
            <Icon name="ChevronDown" size={12} />
            Expand All
          </button>
          <button
            onClick={collapseAll}
            className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground px-2 py-1 rounded hover:bg-muted border border-border"
          >
            <Icon name="ChevronRight" size={12} />
            Collapse All
          </button>
          <span className="text-xs text-muted-foreground">{rows?.length} rows</span>
          <button
            onClick={() => setShowFilters(p => !p)}
            className={`flex items-center gap-1 text-xs px-2 py-1 rounded border transition-colors ${
              hasActiveFilters
                ? 'bg-accent/10 border-accent text-accent' : 'text-muted-foreground hover:text-foreground hover:bg-muted border-border'
            }`}
          >
            <Icon name="Filter" size={12} />
            Filters {hasActiveFilters && '•'}
          </button>
          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className="flex items-center gap-1 text-xs text-muted-foreground hover:text-red-600 px-1 py-1 rounded"
            >
              <Icon name="X" size={11} />
              Clear
            </button>
          )}
          {/* Add top-level deliverable */}
          {onAddChild && (
            <button
              onClick={() => onAddChild(null, 'deliverable')}
              className="flex items-center gap-1 text-xs text-primary hover:text-primary/80 px-2 py-1 rounded hover:bg-primary/10 border border-primary/30 transition-colors"
            >
              <Icon name="Plus" size={12} />
              Add Deliverable
            </button>
          )}
          {/* Adjust Dates button */}
          {onAdjustDates && (
            <button
              onClick={onAdjustDates}
              className="flex items-center gap-1 text-xs text-accent hover:text-accent/80 px-2 py-1 rounded hover:bg-accent/10 border border-accent/30 transition-colors"
              title="Adjust all project dates"
            >
              <Icon name="CalendarClock" size={12} />
              Adjust Dates
            </button>
          )}
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground hidden sm:inline">Dbl-click name • Click status/dates • Drag bar • ⟷ resize edges</span>
          {/* Sort */}
          <select
            value={sortBy}
            onChange={e => setSortBy(e?.target?.value)}
            className="text-xs border border-border rounded px-2 py-1 bg-card text-foreground focus:outline-none focus:ring-1 focus:ring-accent"
          >
            {SORT_OPTIONS?.map(o => <option key={o?.value} value={o?.value}>{o?.label}</option>)}
          </select>
          {/* Zoom */}
          <div className="flex items-center gap-1">
            <button
              onClick={() => setPixelsPerDay(p => Math.max(p - 6, 4))}
              className="p-1 rounded hover:bg-muted border border-border text-muted-foreground hover:text-foreground"
              title="Zoom Out"
            >
              <Icon name="ZoomOut" size={13} />
            </button>
            <span className="text-xs text-muted-foreground w-8 text-center">{pixelsPerDay}px</span>
            <button
              onClick={() => setPixelsPerDay(p => Math.min(p + 6, 80))}
              className="p-1 rounded hover:bg-muted border border-border text-muted-foreground hover:text-foreground"
              title="Zoom In"
            >
              <Icon name="ZoomIn" size={13} />
            </button>
          </div>
          {/* Time Scale Selector */}
          <div className="flex items-center gap-1">
            <Icon name="Calendar" size={12} className="text-muted-foreground" />
            <select
              value={timeScale}
              onChange={(e) => setTimeScale(e?.target?.value)}
              className="text-xs bg-input border border-border rounded px-2 py-0.5 text-foreground focus:outline-none focus:ring-1 focus:ring-ring"
            >
              {TIME_SCALE_OPTIONS?.map(opt => (
                <option key={opt?.value} value={opt?.value}>{opt?.label}</option>
              ))}
            </select>
          </div>
          {/* Column picker */}
          <div className="relative">
            <button
              onClick={() => setShowColumnPicker(p => !p)}
              className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground px-2 py-1 rounded hover:bg-muted border border-border"
            >
              <Icon name="Columns" size={12} />
              Columns
            </button>
            {showColumnPicker && (
              <ColumnPicker
                visibleColumns={visibleColumns}
                onChange={toggleColumn}
                onClose={() => setShowColumnPicker(false)}
              />
            )}
          </div>
        </div>
      </div>
      {/* Filter Bar */}
      {showFilters && (
        <div className="px-3 py-2 border-b border-border bg-muted/10 flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2">
            <label className="text-xs font-medium text-muted-foreground">Resource:</label>
            <select
              value={filterResource}
              onChange={e => setFilterResource(e?.target?.value)}
              className="text-xs border border-border rounded px-2 py-1 bg-card text-foreground focus:outline-none focus:ring-1 focus:ring-accent min-w-[140px]"
            >
              <option value="">All Resources</option>
              {allResourceNames?.map(n => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>
          <div className="flex items-center gap-2">
            <label className="text-xs font-medium text-muted-foreground">Status:</label>
            <select
              value={filterStatus}
              onChange={e => setFilterStatus(e?.target?.value)}
              className="text-xs border border-border rounded px-2 py-1 bg-card text-foreground focus:outline-none focus:ring-1 focus:ring-accent min-w-[120px]"
            >
              <option value="">All Statuses</option>
              {Object.entries(STATUS_CONFIG)?.map(([k, v]) => <option key={k} value={k}>{v?.label}</option>)}
            </select>
          </div>
          <div className="flex items-center gap-2">
            <label className="text-xs font-medium text-muted-foreground">Type:</label>
            <select
              value={filterType}
              onChange={e => setFilterType(e?.target?.value)}
              className="text-xs border border-border rounded px-2 py-1 bg-card text-foreground focus:outline-none focus:ring-1 focus:ring-accent min-w-[120px]"
            >
              <option value="">All Types</option>
              <option value="deliverable">Deliverable</option>
              <option value="task">Task</option>
              <option value="subtask">Sub-task</option>
            </select>
          </div>
        </div>
      )}
      {/* ── MAIN GANTT CHART ── */}
      <div className="flex" style={{ maxHeight: 'calc(100vh - 360px)' }}>
        {/* LEFT PANEL — fixed width, vertical scroll only */}
        <div
          ref={leftPanelRef}
          className="flex-shrink-0 overflow-y-auto overflow-x-hidden border-r border-border"
          style={{ width: `${totalFixedWidth}px` }}
          onScroll={() => syncScroll(leftPanelRef, rightPanelRef)}
        >
          {/* Left header */}
          <div className="flex border-b border-border sticky top-0 bg-card z-20" style={{ height: `${HEADER_HEIGHT}px` }}>
            <div
              className="flex-shrink-0 flex items-end pb-1 border-r border-border bg-muted px-2 relative"
              style={{ width: `${nameColWidth}px` }}
            >
              <span className="text-xs font-semibold text-foreground">Name</span>
              <ResizeHandle onResize={handleNameColResize} />
            </div>
            {ALL_COLUMNS?.filter(c => visibleColumns?.includes(c?.id))?.map(col => (
              <div
                key={col?.id}
                className="flex-shrink-0 flex items-end pb-1 border-r border-border bg-muted px-2 relative"
                style={{ width: `${colW(col?.id)}px` }}
              >
                <span className="text-xs font-semibold text-foreground">{col?.label}</span>
                <ResizeHandle onResize={(delta) => handleColResize(col?.id, delta, col?.minWidth)} />
              </div>
            ))}
          </div>

          {/* Left rows */}
          {rows?.length === 0 ? (
            <div className="py-12 text-center text-muted-foreground">
              <Icon name="SearchX" size={32} className="mx-auto mb-2 opacity-40" />
              <p className="text-sm">No items match the current filters.</p>
            </div>
          ) : (
            rows?.map((row) => renderLeftRow(row, false))
          )}
        </div>

        {/* RIGHT PANEL — timeline, horizontal + vertical scroll */}
        <div
          ref={rightPanelRef}
          className="flex-1 overflow-x-auto overflow-y-auto min-w-0"
          onScroll={(e) => {
            syncScroll(rightPanelRef, leftPanelRef);
            // Sync horizontal scroll to resource section
            if (resRightPanelRef?.current) {
              resRightPanelRef.current.scrollLeft = e?.currentTarget?.scrollLeft;
            }
          }}
        >
          {/* Timeline header */}
          <div className="flex flex-col border-b border-border sticky top-0 bg-card z-20" style={{ height: `${HEADER_HEIGHT}px`, minWidth: `${totalTimelineWidth}px` }}>
            <div className="flex border-b border-border bg-muted/80" style={{ height: '20px' }}>
              {timeScaleHeaders?.topGroups?.map((mg, i) => {
                const startIdx = timeScaleHeaders?.topGroups?.slice(0, i)?.reduce((s, g) => s + g?.count, 0);
                const groupWidth = timeScaleHeaders?.bottomUnits?.slice(startIdx, startIdx + mg?.count)?.reduce((s, u) => s + u?.width, 0) || mg?.count * pixelsPerDay;
                return (
                  <div key={i} className="flex-shrink-0 flex items-center justify-center border-r border-border" style={{ width: `${groupWidth}px` }}>
                    <span className="text-xs font-semibold text-foreground truncate px-1">{mg?.label}</span>
                  </div>
                );
              })}
            </div>
            <div className="flex bg-muted" style={{ height: '24px' }}>
              {timeScaleHeaders?.bottomUnits?.map((unit, i) => (
                <div key={i} className={`flex-shrink-0 flex items-center justify-center border-r border-border ${unit?.isWeekend ? 'bg-muted/60' : 'bg-muted/20'}`} style={{ width: `${unit?.width}px` }}>
                  {(timeScale !== 'daily' || pixelsPerDay >= 16) && (
                    <span className="text-muted-foreground truncate px-0.5" style={{ fontSize: '10px' }}>
                      {timeScale === 'daily' ? unit?.label :
                       timeScale === 'weekly' ? (unit?.width >= 60 ? unit?.label : format(unit?.date, 'MMM d')) :
                       unit?.label}
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Timeline rows */}
          {rows?.length === 0 ? (
            <div className="py-12 text-center text-muted-foreground" style={{ width: `${totalTimelineWidth}px` }}>
              <Icon name="SearchX" size={32} className="mx-auto mb-2 opacity-40" />
              <p className="text-sm">No items match the current filters.</p>
            </div>
          ) : (
            rows?.map((row) => renderRightRow(row, false))
          )}
        </div>
      </div>
      {/* ── RESOURCE UTILIZATION SECTION ── */}
      {resourceRows?.length > 0 && (
        <div className="border-t-2 border-indigo-200">
          {/* Section header */}
          <div
            className="flex items-center gap-2 px-3 py-2 bg-indigo-50/60 border-b border-indigo-200 cursor-pointer select-none"
            onClick={() => setResourceSectionCollapsed(p => !p)}
          >
            <Icon
              name={resourceSectionCollapsed ? 'ChevronRight' : 'ChevronDown'}
              size={14}
              className="text-indigo-600 flex-shrink-0"
            />
            <div className="w-2 h-2 rounded-full bg-indigo-400 flex-shrink-0" />
            <span className="text-xs font-semibold text-indigo-800">Resource Utilization</span>
            <span className="text-xs text-indigo-500 ml-1">({resourceRows?.length} resource{resourceRows?.length !== 1 ? 's' : ''})</span>
            {resourceRows?.some(r => r?._hasConflict) && (
              <span
                className="flex items-center gap-1 bg-red-100 text-red-700 border border-red-300 rounded-full px-2 py-0.5 text-xs font-semibold ml-1"
                title="Some resources have overlapping allocations exceeding capacity"
              >
                <Icon name="AlertTriangle" size={11} className="text-red-600" />
                {resourceRows?.filter(r => r?._hasConflict)?.length} over capacity
              </span>
            )}
            <span className="ml-auto text-xs text-indigo-400">Allocation bars show scheduled work per resource</span>
          </div>

          {!resourceSectionCollapsed && (
            <div className="flex" style={{ maxHeight: '220px' }}>
              {/* Resource left panel */}
              <div
                ref={resLeftPanelRef}
                className="flex-shrink-0 overflow-y-auto overflow-x-hidden border-r border-border"
                style={{ width: `${totalFixedWidth}px` }}
                onScroll={() => syncResScroll(resLeftPanelRef, resRightPanelRef)}
              >
                {/* Left header for resource section */}
                <div className="flex border-b border-border sticky top-0 bg-indigo-50/80 z-20" style={{ height: `${HEADER_HEIGHT}px` }}>
                  <div
                    className="flex-shrink-0 flex items-end pb-1 border-r border-border bg-indigo-100/60 px-2 relative"
                    style={{ width: `${nameColWidth}px` }}
                  >
                    <span className="text-xs font-semibold text-indigo-700">Resource</span>
                  </div>
                  {ALL_COLUMNS?.filter(c => visibleColumns?.includes(c?.id))?.map(col => (
                    <div
                      key={col?.id}
                      className="flex-shrink-0 flex items-end pb-1 border-r border-border bg-indigo-100/60 px-2 relative"
                      style={{ width: `${colW(col?.id)}px` }}
                    >
                      <span className="text-xs font-semibold text-indigo-700">{col?.label}</span>
                    </div>
                  ))}
                </div>
                {resourceRows?.map((row) => renderLeftRow(row, true))}
              </div>

              {/* Resource right panel (timeline) */}
              <div
                ref={resRightPanelRef}
                className="flex-1 overflow-x-auto overflow-y-auto min-w-0"
                onScroll={(e) => {
                  syncResScroll(resRightPanelRef, resLeftPanelRef);
                  // Sync horizontal scroll back to main timeline
                  if (rightPanelRef?.current) {
                    rightPanelRef.current.scrollLeft = e?.currentTarget?.scrollLeft;
                  }
                }}
              >
                {/* Timeline header (resource section) */}
                <div className="flex flex-col border-b border-border sticky top-0 bg-indigo-50/80 z-20" style={{ height: `${HEADER_HEIGHT}px`, minWidth: `${totalTimelineWidth}px` }}>
                  <div className="flex border-b border-border bg-indigo-100/40" style={{ height: '20px' }}>
                    {timeScaleHeaders?.topGroups?.map((mg, i) => {
                      const startIdx = timeScaleHeaders?.topGroups?.slice(0, i)?.reduce((s, g) => s + g?.count, 0);
                      const groupWidth = timeScaleHeaders?.bottomUnits?.slice(startIdx, startIdx + mg?.count)?.reduce((s, u) => s + u?.width, 0) || mg?.count * pixelsPerDay;
                      return (
                        <div key={i} className="flex-shrink-0 flex items-center justify-center border-r border-border" style={{ width: `${groupWidth}px` }}>
                          <span className="text-xs font-semibold text-indigo-600 truncate px-1">{mg?.label}</span>
                        </div>
                      );
                    })}
                  </div>
                  <div className="flex bg-indigo-50/60" style={{ height: '24px' }}>
                    {timeScaleHeaders?.bottomUnits?.map((unit, i) => (
                      <div key={i} className={`flex-shrink-0 flex items-center justify-center border-r border-border ${unit?.isWeekend ? 'bg-indigo-100/40' : ''}`} style={{ width: `${unit?.width}px` }}>
                        {(timeScale !== 'daily' || pixelsPerDay >= 16) && (
                          <span className="text-indigo-400 truncate px-0.5" style={{ fontSize: '10px' }}>
                            {timeScale === 'daily' ? unit?.label :
                             timeScale === 'weekly' ? (unit?.width >= 60 ? unit?.label : format(unit?.date, 'MMM d')) :
                             unit?.label}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
                {resourceRows?.map((row) => renderRightRow(row, true))}
              </div>
            </div>
          )}
        </div>
      )}
      {/* Legend */}
      <div className="px-3 py-2 border-t border-border bg-muted/10 flex flex-wrap items-center gap-4">
        <span className="text-xs font-medium text-muted-foreground">Legend:</span>
        {[
          { color: 'bg-purple-500', label: 'Deliverable' },
          { color: 'bg-blue-500', label: 'Task' },
          { color: 'bg-emerald-500', label: 'Sub-task' },
          { color: 'bg-indigo-400', label: 'Resource Allocation' },
          { color: 'bg-amber-300/60 border border-amber-500', label: 'Phase' },
        ]?.map(item => (
          <div key={item?.label} className="flex items-center gap-1.5">
            <div className={`w-3 h-3 rounded ${item?.color}`} />
            <span className="text-xs text-muted-foreground">{item?.label}</span>
          </div>
        ))}
        <div className="flex items-center gap-1.5">
          <span className="flex items-center gap-0.5 bg-red-100 text-red-700 border border-red-300 rounded-full px-1.5 py-0 leading-4 font-semibold" style={{ fontSize: '10px' }}>
            <Icon name="AlertTriangle" size={9} className="text-red-600" />
            N
          </span>
          <span className="text-xs text-muted-foreground">Over Capacity</span>
        </div>
        <div className="ml-auto flex items-center gap-3 text-xs text-muted-foreground">
          <span>✏️ Dbl-click name to rename</span>
          <span>🎯 Click status to change</span>
          <span>📅 Click dates to edit</span>
          <span>⟷ Drag bar edges to resize</span>
        </div>
      </div>
      {/* Tooltip */}
      <GanttTooltip
        row={tooltip?.row}
        visible={tooltip?.visible}
        x={tooltip?.x}
        y={tooltip?.y}
      />
      {/* Date editor popover */}
      {dateEditor?.visible && (
        <DateEditPopover
          row={dateEditor?.row}
          x={dateEditor?.x}
          y={dateEditor?.y}
          onSave={handleDateSave}
          onClose={() => setDateEditor({ visible: false, row: null, x: 0, y: 0 })}
          saving={savingDateId === dateEditor?.row?.id}
        />
      )}
    </div>
  );
};

export default ResourceGanttView;
